import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ModalRoot,
  ModalPortal,
  ModalOverlay,
  ModalContent,
  ModalContentHeader,
  ModalContentBody,
  ModalContentFooter,
  ModalContentActions,
  ModalCancelButton,
  ModalActionButton,
  TextInput,
} from '@socialchorus/shared-ui-components';
import './styles.scss';

const ALT_MAX_LENGTH = 100;

export interface AddImageValues {
  url: string;
  altText: string;
}

interface AddImageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: AddImageValues) => void;
  initialUrl?: string;
  initialAltText?: string;
}

export default function AddImageModal({
  open,
  onOpenChange,
  onSubmit,
  initialUrl = '',
  initialAltText = '',
}: AddImageModalProps) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [altText, setAltText] = useState('');

  // Seed the fields from the selected image each time the modal is opened.
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setAltText(initialAltText);
    }
  }, [open, initialUrl, initialAltText]);

  const trimmedUrl = url.trim();
  const canSubmit = trimmedUrl.length > 0;
  // A pre-populated URL means we are editing an existing image.
  const isEditing = initialUrl.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ url: trimmedUrl, altText: altText.trim() });
    onOpenChange(false);
  };

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange} mode="dialog">
      <ModalPortal>
        <ModalOverlay />
        <ModalContent size="small">
          <ModalContentHeader
            title={t('user_generated_content.editor.add_image.title')}
          />
          <ModalContentBody>
            <TextInput
              label={t('user_generated_content.editor.add_image.url_label')}
              placeholder={t(
                'user_generated_content.editor.add_image.url_placeholder'
              )}
              type="url"
              value={url}
              onChange={setUrl}
              showCharacterCounter={false}
              fullWidth
              // Focusing a field when the dialog opens is intentional UX.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className="lexical-rich-text-editor__add-image-url"
            />
            <TextInput
              label={t('user_generated_content.editor.add_image.alt_label')}
              placeholder={t(
                'user_generated_content.editor.add_image.alt_placeholder'
              )}
              value={altText}
              onChange={setAltText}
              maxLength={ALT_MAX_LENGTH}
              showCharacterCounter
              fullWidth
            />
          </ModalContentBody>
          <ModalContentFooter>
            <ModalContentActions>
              <ModalCancelButton label={t('common.cancel')} />
              <ModalActionButton
                label={isEditing ? t('common.apply') : t('common.add')}
                onClick={handleSubmit}
                disabled={!canSubmit}
              />
            </ModalContentActions>
          </ModalContentFooter>
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
}
